<template>
  <div :id="`${source}_audience_type_selector`" class="flex flex-col gap-6">
    <div id="audience_type" class="form_col">
      <span>Audience</span>
      <CustomSelector
        v-model="campaignType"
        :source="`${source}_audience_type_selector`"
        :options="filteredTypeOptions"
        :direction="customSelectorDirections.HORIZONTAL"
      />
    </div>

    <div
      id="list"
      class="form_col"
      v-if="campaignType === campaignTypes.MANUAL_LIST"
    >
      <span>List</span>
      <a-select
        show-search
        :filter-option="filterDropdown"
        v-model:value="formData.entityListId"
        placeholder="Select List"
        option-label-prop="label"
      >
        <template #suffixIcon>
          <ThemeImage
            :icon="imageNames.BOTTOM_ARROW"
            alt="bottom_white_arrow"
            class="size-4"
          />
        </template>

        <a-select-option
          v-for="(list, index) in listItems"
          :key="`${list.id}_list_${index}`"
          :value="list.id"
          :label="list.list_name"
        >
          <div class="flex items-center gap-2">
            <span>{{ list.list_name }}</span>
            <PrivateTag v-if="list.private_flag" />
          </div>
        </a-select-option
        >
      </a-select>
    </div>

    <div
      id="list"
      class="form_col"
      v-if="campaignType === campaignTypes.MANUAL"
    >
      <span>Upload Contacts</span>

      <UploadCSVComponent
        v-if="formData.entityListId"
        source="campaigns"
        height="96px"
        :entity-list-id="formData.entityListId"
        @import-success="handleImportSuccess"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { message } from "ant-design-vue";
import { campaignTypes } from "@/src/constants/campaignConstants.js";
import { callCampaignAudienceTypeOptions } from "@/src/constants/callConstants.js";

import DashboardService from "@/src/api/DashboardService.js";
import CustomSelector from "@/src/components/CustomSelector.vue";
import UploadCSVComponent from "@/src/components/upload_components/UploadCSVComponent.vue";
import WorkspaceService from "@/src/api/WorkspaceService.js";
import { customSelectorDirections } from "@/src/constants/customComponentConstants.js";
import { filterDropdown } from "@/src/utils/utils.js";
import store from "@/src/store/index.js";
import PrivateTag from "@/src/components/styled_components/PrivateTag.vue";

import { imageNames } from "@/src/constants/imageConstants.js";

const props = defineProps({
  source: { type: String, required: true },
  formData: {
    type: Object,
    required: true,
    default: () => ({
      name: "",
      callVia: null,
      entityListId: null,
    }),
  },
});

const route = useRoute();

const campaignType = ref(null);
const listItems = ref([]);
const hasListAccess = computed(() => store.getters["rbac/hasListsAccess"]);
const hasImportAccess = computed(() => store.getters["rbac/hasImportAccess"]);

const filteredTypeOptions = computed(() => {
  return callCampaignAudienceTypeOptions.filter((option) => {
    if (option.id === campaignTypes.MANUAL_LIST) {
      return hasListAccess.value;
    } else if (option.id === campaignTypes.MANUAL) {
      return hasImportAccess.value;
    }
    return true;
  });
});
const handleImportSuccess = async () => {
  message.success("CSV import in progress. Large files can take a few minutes.");
};

const toGetAllEntityLists = async () => {
  try {
    const params = {
      entity_type: ["lead", "contact"],
    };

    const response = await DashboardService.getAllEntityListsBySource(params);
    if (response?.success) {
      listItems.value = [
        ...(response?.shared_lists || []),
        ...(response?.private_lists || [])
      ];
    }
  } catch (error) {
    console.error("Error occurred while fetching the entity lists: ", error);
  }
};

const createEntityList = async () => {
  try {
    const payload = {
      list_name: "call_campaign_" + WorkspaceService.getCurrentWorkspaceId(),
      entity_type: "lead",
      private_flag: false,
      workspace_id: -1,
    };
    const response = await DashboardService.createEntityList(payload);
    if (response?.success) {
      props.formData.entityListId = response?.entity_list;
    }
  } catch (error) {
    console.error("Error creating entity list:", error);
    message.error("An error occurred while creating the entity list");
  }
};

watch(campaignType, (newVal) => {
  if (newVal === campaignTypes.MANUAL) {
    // skip if created from calls dialer bulk-selection flow
    // (AddToDialerCampaign opens this page with the broadcast sessionId
    // as `query.id`; entities are pushed directly to the Call Service
    // post-create, so no placeholder CRM list is needed)
    if (
      route.name === "sales-dialer-campaign-create" &&
      !!route.query.id
    ) {
      return;
    }
    createEntityList();
  } else if (newVal !== campaignTypes.MANUAL_LIST) {
    props.formData.entityListId = null;
  }
});

onMounted(() => {
  toGetAllEntityLists();
  if (props.formData.entityListId) {
    campaignType.value = campaignTypes.MANUAL_LIST;
  }
});
</script>
